import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { personIsGuest } from '~app/utilities/guest.utility';
import { PersonService } from './person.service';

/**
 * Resolves which people should show the guest badge, by person id.
 *
 * A person is a "guest" when they do NOT have a usda.gov email on their
 * profile. Many UI surfaces (dropdowns, the trips/search table) only have a
 * slim person id to work with, so this service derives the set of guest ids
 * from `PersonService.allPeople` - which carries the backend `isUSDAUser`
 * virtual - and exposes it for id-based lookups.
 *
 * For objects that already carry an email signal (`isUSDAUser` or a `users`
 * array), prefer the synchronous `personIsGuest()` utility instead.
 *
 * Scope limitation: the lookup is keyed by the ids in
 * `PersonService.allPeople` (the all-person dropdown list). People referenced
 * from a context not in that set won't resolve here, so the badge won't render
 * for them. For high-fidelity surfaces, bind `[person]` with the person's
 * email data so the synchronous utility path is taken.
 */
@Injectable({
  providedIn: 'root',
})
export class GuestRoleService {
  /**
   * Set of person ids who do not have a usda.gov email (i.e. guests).
   * Recomputes whenever `allPeople` reloads (e.g. after a profile/email save).
   */
  public readonly guestPersonIds$: Observable<ReadonlySet<string>> =
    this.personService.allPeople.pipe(
      map((people) => {
        const ids = new Set<string>();
        people.forEach((person) => {
          if (personIsGuest(person) === true) {
            ids.add(person.id);
          }
        });
        return ids;
      }),
      shareReplay(1)
    );

  constructor(private personService: PersonService) {}

  /** Whether the given person id is a guest (no usda.gov email). */
  isGuestPerson$(personId: string): Observable<boolean> {
    return this.guestPersonIds$.pipe(
      map((ids) => !!personId && ids.has(personId))
    );
  }
}
